import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    IconButton,
    DialogContent,
    Button,
    Typography,
    TextField,
    Select,
    MenuItem,
    SelectChangeEvent,
    useTheme
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { toast } from 'sonner';
import { PlanBenefitsType, SectionOFCover } from 'Application/models/enums';
import { ExchangeRateParams } from 'Application/models';
import { checkIfInvalidRate, getKeyFromObjectValue } from 'Application/utils';
import { ThemeMode } from 'config';
import { useExchangeRateStore } from 'Application/store';

interface AddNewExchangeRate {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    setIsAddedRate: React.Dispatch<React.SetStateAction<boolean>>;
}

const AddExchangeRate: React.FC<AddNewExchangeRate> = ({ isOpen, setIsOpen, setIsAddedRate }) => {
    const initialFormData: ExchangeRateParams = {
        fromCurrency: '',
        toCurrency: '',
        rate: 0
    };
    const [formData, setFormData] = useState<ExchangeRateParams>(initialFormData);

    const { fromCurrency, toCurrency, rate } = formData;
    const fromCurrencyFieldName = getKeyFromObjectValue(formData, fromCurrency);
    const toCurrencyFieldName = getKeyFromObjectValue(formData, toCurrency);
    const rateFieldName = getKeyFromObjectValue(formData, rate);
    const ifInvalidRate = checkIfInvalidRate(rate);

    const { isLoading, addExchangeRate } = useExchangeRateStore();

    const { palette } = useTheme();
    const modalTitleColor = palette.mode === ThemeMode.DARK ? '#A0AEC0' : '#4A5568';

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;

        // Set rate to zero if rate is edited and value is equal to empty string
        if (name === 'rate' && value === '') {
            setFormData({
                ...formData,
                rate: initialFormData.rate
            });
            return;
        }

        // If rate is edited and value is less than zero, set rate to zero.
        const newValue =
            name === 'fromCurrency' || name === 'toCurrency' ? value : parseFloat(value) < 0 ? initialFormData.rate : parseFloat(value);

        setFormData({
            ...formData,
            [name]: newValue
        });

        /* Used for removing leading zero from user's side when previous input value is just zero 
        (that is, from "02" to "2"). */
        event.target.value = name === 'rate' ? Number(value).toString() : value;
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        try {
            const { fromCurrency, toCurrency, rate } = formData;

            await addExchangeRate(fromCurrency, toCurrency, rate);
            toast.success('Exchange rate created successfully');
            setIsOpen(false);
            setIsAddedRate(true);

            // Clear the form data after successful creation
            setFormData({
                fromCurrency: '',
                toCurrency: '',
                rate: 0
            });
        } catch (error) {
            toast.error(`Failed to create exchange rate: ${error}`);
        }
    };

    const handleClose = () => {
        setIsOpen(false);
        setFormData(initialFormData); // Reset to the initial exchange rate if modal is closed.
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    width: '70%',
                    maxWidth: '600px'
                }
            }}
        >
            <DialogTitle>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: modalTitleColor }}>Create a new exchange rate</span>
                    <IconButton onClick={handleClose}>
                        <ClearIcon />
                    </IconButton>
                </div>
                <div style={{ marginTop: '0.5rem', color: '#718096', fontSize: '0.875rem' }}>Please fill in the following details</div>
            </DialogTitle>
            <DialogContent>
                <form onSubmit={handleSubmit}>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            From Currency
                        </Typography>
                        <TextField
                            placeholder="USD"
                            required
                            name={fromCurrencyFieldName}
                            variant="outlined"
                            value={fromCurrency}
                            onChange={handleInputChange}
                            fullWidth
                        />
                    </div>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            To Currency
                        </Typography>
                        <TextField
                            placeholder="USD"
                            required
                            name={toCurrencyFieldName}
                            variant="outlined"
                            value={toCurrency}
                            onChange={handleInputChange}
                            fullWidth
                        />
                    </div>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Exchange Rate
                        </Typography>
                        <TextField
                            placeholder="131.23"
                            required
                            type="number"
                            name={rateFieldName}
                            variant="outlined"
                            value={rate}
                            onChange={handleInputChange}
                            fullWidth
                        />
                    </div>
                    {ifInvalidRate && (
                        <Typography style={{ color: '#ff0000' }}>Exchange rate should not have more than two decimal places.</Typography>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            sx={{
                                color: '#4A5568',
                                borderColor: '#4A5568',
                                backgroundColor: '#ffffff'
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="outlined"
                            sx={{
                                backgroundColor: '#673ab7',
                                color: '#ffffff',
                                marginLeft: '1.5rem',
                                '&:hover': {
                                    backgroundColor: '#ffffff',
                                    color: '#673ab7',
                                    borderColor: '#673ab7'
                                },
                                '&.Mui-disabled': {
                                    color: '#ffffff',
                                    backgroundColor: '#98A2B3'
                                }
                            }}
                            disabled={ifInvalidRate || isLoading}
                        >
                            Submit
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default AddExchangeRate;
